"use client";

import { useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { SignupFlowSlug } from "libs/state/src/lib/stores/signupFlow/constants";
import { useSignupFlowErrorStore, useSignupFlowStore } from "@./state";
import { useQueryParams } from "apps/website/hooks/useQueryParams";

const INITIAL_SLUG = SignupFlowSlug.CAT_NAMES;

export function useSignupFlowReset(): void {
  const pathname = usePathname();
  const router = useRouter();
  const { chainQueryParamsToUrl } = useQueryParams();
  const hasHandledResetRef = useRef<string | null>(null);

  const {
    isStoreInitialised,
    versionMismatchDetected,
    getActiveCatIds,
  } = useSignupFlowStore((state) => ({
    isStoreInitialised: state.isStoreInitialised,
    versionMismatchDetected: state.versionMismatchDetected,
    getActiveCatIds: state.getActiveCatIds,
  }));

  useEffect(() => {
    if (!isStoreInitialised) {
      return;
    }

    // Reset the ref when pathname changes (new page navigation)
    if (hasHandledResetRef.current !== pathname) {
      hasHandledResetRef.current = null;
    }

    // Skip if we've already handled a reset for this pathname
    if (hasHandledResetRef.current === pathname) {
      return;
    }

    // Always reset store if version mismatch detected (even on initial slug)
    // This ensures old incompatible data is cleared immediately
    if (versionMismatchDetected) {
      useSignupFlowStore.getState().resetSignupFlow();
      useSignupFlowErrorStore.getState().clearAll();
      hasHandledResetRef.current = pathname;

      // Only redirect if not already on initial slug
      if (pathname !== INITIAL_SLUG) {
        const redirectUrl = chainQueryParamsToUrl(INITIAL_SLUG);
        router.replace(redirectUrl);
      }
      return;
    }

    // If no cats and not on initial slug, reset and redirect to start
    if (getActiveCatIds().length === 0 && pathname !== INITIAL_SLUG) {
      useSignupFlowStore.getState().resetSignupFlow();
      useSignupFlowErrorStore.getState().clearAll();
      hasHandledResetRef.current = pathname;

      const redirectUrl = chainQueryParamsToUrl(INITIAL_SLUG);
      router.replace(redirectUrl);
    }
  }, [
    isStoreInitialised,
    versionMismatchDetected,
    pathname,
    router,
    chainQueryParamsToUrl,
    getActiveCatIds,
  ]);
}
